import { Link } from "react-router-dom";
import { HeroFull } from '@/components/HeroFull';
import { HERO_IMAGE_DEFAULT } from '@/config/images';
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { NeighborCities } from "@/components/feature/NeighborCities";
import SEO from "@/components/feature/SEO";

const PUEBLOS_GUADALHORCE = [
  { name: "Almogía", desc: "Pueblo blanco al norte de Málaga, conocido por sus almendros. Persianas tradicionales y modernas." },
  { name: "Pizarra", desc: "Pueblo agrícola con vega fértil. Trabajamos persianas en cortijos y nuevas urbanizaciones." },
  { name: "Casarabonela", desc: "Pueblo blanco a 500m de altitud. Persianas resistentes a la amplitud térmica." },
  { name: "Carratraca", desc: "Conocido por sus baños termales. Reparación de persianas en hoteles y viviendas." },
  { name: "Ardales", desc: "Cerca del Caminito del Rey. Persianas en alojamientos rurales y vivienda local." },
  { name: "Valle de Abdalajís", desc: "Pueblo al pie de la sierra. Especialistas en persianas resistentes al viento." },
  { name: "Cártama Estación", desc: "Núcleo en crecimiento residencial. Persianas modernas en viviendas nuevas." },
  { name: "Pizarra norte", desc: "Urbanizaciones nuevas con persianas motorizadas Somfy y Becker." },
];

export default function GuadalhorceHub() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Persianas en el Valle del Guadalhorce | Cártama, Coín, Álora"
        description="Reparación e instalación de persianas, toldos y mosquiteras en el Valle del Guadalhorce: Cártama, Coín, Álora, Alhaurín y pueblos del interior de Málaga."
        canonical="/zonas/valle-del-guadalhorce/"
        ogType="website"
        breadcrumbs={[
          { name: "Inicio", path: "/" },
          { name: "Zonas", path: "/zonas" },
          { name: "Valle del Guadalhorce", path: "/zonas/valle-del-guadalhorce" },
        ]}
      />
      <Header />

      <main id="main-content">
        <Breadcrumbs
          items={[
            { name: "Inicio", path: "/" },
            { name: "Zonas", path: "/zonas" },
            { name: "Valle del Guadalhorce" },
          ]}
        />
        <HeroFull
          imageSrc={HERO_IMAGE_DEFAULT}
          imageAlt="Persianas Toldos Málaga atendiendo el Valle del Guadalhorce"
          badge="🏞️ Cobertura valle del Guadalhorce"
          title="Persianas, Toldos y Mosquiteras en el Valle del Guadalhorce"
          subtitle="Cubrimos Cártama, Coín, Álora, Alhaurín y pueblos del interior. Técnico propio, sin intermediarios."
          primaryCta={{ href: "tel:+34951120770", label: "Llamar: 951 12 07 70" }}
          secondaryCta={{ href: "https://wa.me/34627213347", label: "💬 WhatsApp" }}
          trustSignals={["Visitas agrupadas 2x/sem", "Presupuesto gratis", "Garantía 2 años"]}
        />

        {/* ¿Qué es el Valle del Guadalhorce? */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              ¿Qué es el Valle del Guadalhorce?
            </h2>
            <div className="max-w-3xl">
              <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-4">
                El Valle del Guadalhorce es la comarca formada por los municipios bañados por el río Guadalhorce, al norte y oeste de Málaga capital. Con unos 175.000 habitantes y 11 municipios, es la huerta tradicional de Málaga y una de las zonas de mayor crecimiento residencial gracias a su proximidad al aeropuerto y a la capital.
              </p>
              <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed">
                El clima es continental moderado, con veranos más cálidos y secos que en la costa, lo que requiere persianas con buen aislamiento térmico. Trabajamos tanto en cortijos agrícolas tradicionales como en urbanizaciones modernas de Cártama Estación, Alhaurín de la Torre y Coín.
              </p>
            </div>
          </div>
        </section>

        {/* Pueblos cubiertos */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-6">
              Pueblos cubiertos en el Valle del Guadalhorce
            </h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-8 max-w-3xl">
              Reparamos persianas en todos los pueblos del Guadalhorce. Visitas agrupadas 2 veces por semana para optimizar desplazamiento.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl">
              {PUEBLOS_GUADALHORCE.map((p) => (
                <div
                  key={p.name}
                  className="bg-surface-alt rounded-2xl p-6 border border-border hover:border-primary transition-colors"
                >
                  <h3 className="font-heading font-bold text-base text-text-primary mb-2">{p.name}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 max-w-3xl">
              <p className="font-body text-base text-text-secondary leading-relaxed">
                Las cabeceras comarcales tienen city pages dedicadas:{" "}
                <Link to="/persianas/cartama" className="text-primary hover:underline font-semibold">Cártama</Link>,{" "}
                <Link to="/persianas/coin" className="text-primary hover:underline font-semibold">Coín</Link>,{" "}
                <Link to="/persianas/alora" className="text-primary hover:underline font-semibold">Álora</Link>,{" "}
                <Link to="/persianas/alhaurin-de-la-torre" className="text-primary hover:underline font-semibold">Alhaurín de la Torre</Link>{" "}
                y{" "}
                <Link to="/persianas/alhaurin-el-grande" className="text-primary hover:underline font-semibold">Alhaurín el Grande</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* Servicios */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-6">
              Servicios en todo el Valle del Guadalhorce
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-3">Persianas</h3>
                <ul className="space-y-2 font-body text-sm text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Reparación de motores tubulares</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Cambio de cinta o enrollador</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Sustitución de lamas (aluminio y PVC)</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Motorización de persianas manuales</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-3">Toldos</h3>
                <ul className="space-y-2 font-body text-sm text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Instalación toldos extensibles</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Toldos cofre motorizados</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Reparación de brazos y lonas</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Pérgolas bioclimáticas</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-3">Mosquiteras</h3>
                <ul className="space-y-2 font-body text-sm text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Mosquiteras enrollables</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Mosquiteras correderas</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Mosquiteras fijas a medida</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Cambio de tela en mosquiteras</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tarifas */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              Tarifas zona Guadalhorce interior
            </h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-8 max-w-2xl">
              Precios aplicables a toda la comarca del Valle del Guadalhorce. Visitas agrupadas para optimizar costes de desplazamiento.
            </p>
            <div className="max-w-2xl mx-auto bg-surface-alt rounded-3xl border border-border overflow-hidden">
              <div className="divide-y divide-border">
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Cambiar cinta persiana</span>
                  <span className="font-heading font-bold text-lg text-primary">75€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Reparar motor persiana</span>
                  <span className="font-heading font-bold text-lg text-primary">95€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Sustituir motor</span>
                  <span className="font-heading font-bold text-lg text-primary">165€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Cambiar lamas (por lama)</span>
                  <span className="font-heading font-bold text-lg text-primary">15€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Desplazamiento</span>
                  <span className="font-heading font-bold text-lg text-primary">+10-15€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Revisión + presupuesto</span>
                  <span className="font-heading font-bold text-lg text-green-600">GRATIS</span>
                </div>
              </div>
              <div className="px-6 md:px-8 py-4 bg-surface-alt border-t border-border">
                <p className="font-body text-sm text-text-secondary text-center">
                  Visitas agrupadas en el Valle del Guadalhorce. Si tienes vecinos con persianas a reparar, podemos optimizar el desplazamiento y reducir costes para todos.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Clima local */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              Clima local y mantenimiento
            </h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed max-w-3xl">
              El Valle del Guadalhorce presenta amplitud térmica notable: veranos por encima de 38°C y heladas ocasionales en invierno. Esto somete las persianas a contracciones y dilataciones que aceleran el desgaste de cintas y empalmes de PVC. Recomendamos revisar mecanismos cada 24 meses y optar por aluminio en sustituciones.
            </p>
          </div>
        </section>

        <NeighborCities slug="valle-del-guadalhorce" service="persianas" />

        {/* CTA Final */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
                ¿Necesitas reparar tu persiana en el Valle del Guadalhorce?
              </h2>
              <p className="font-body text-lg text-text-secondary mb-8">
                Llámanos al 951 12 07 70 o pide presupuesto online. Visitamos Cártama, Coín, Álora, Alhaurín y todos los pueblos de la comarca.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="tel:+34951120770"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-8 py-5 rounded-pill transition-colors whitespace-nowrap touch-target"
                >
                  <span aria-hidden="true">📞</span> 951 12 07 70
                </a>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info%20sobre%20persianas%20en%20el%20Valle%20del%20Guadalhorce"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors whitespace-nowrap"
                >
                  <span aria-hidden="true">💬</span> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}